import { config } from "@/shared/lib/config";
import { fetcher } from "@/shared/lib/fetcher";

export type ClassEnrollmentClassDTO = {
  templateId: string;
  classTypeName: string;
  levelName: string;
};

export type ClassEnrollmentListItemDTO = {
  id: string;
  fullName: string;
  nickname: string | null;
  phoneNumber: string;
  status: string;
  submittedAt: string;
  studentId: string | null;
  classes: ClassEnrollmentClassDTO[];
};

export type ClassEnrollmentAnswerDTO = {
  fieldId: string;
  label: string;
  type: string;
  value: string | null;
};

export type ClassEnrollmentDetailDTO = ClassEnrollmentListItemDTO & {
  dateOfBirth: string;
  gender: string;
  adminNotes: string | null;
  formResponseId: string;
  answers: ClassEnrollmentAnswerDTO[];
  student: { id: string; memberId: string; name: string } | null;
};

export type ClassEnrollmentSimilarStudentDTO = {
  id: string;
  memberId: string;
  name: string;
  phoneNumber: string | null;
  similarityPercent: number;
};

export const classEnrollmentsApi = {
  list: (query: { limit: number; offset: number; status?: string; phone?: string }) => {
    const url = new URL(`${config.apiUrl}/class-enrollments`);
    url.searchParams.set("limit", String(query.limit));
    url.searchParams.set("offset", String(query.offset));
    if (query.status) url.searchParams.set("status", query.status);
    if (query.phone) url.searchParams.set("phone", query.phone);
    return fetcher.get<{
      data: ClassEnrollmentListItemDTO[];
      meta: { total: number; limit: number; offset: number };
    }>(url.toString());
  },

  unconfirmedCount: () =>
    fetcher.get<{ count: number }>(`${config.apiUrl}/class-enrollments/unconfirmed-count`),

  get: (id: string) =>
    fetcher.get<ClassEnrollmentDetailDTO>(`${config.apiUrl}/class-enrollments/${id}`),

  updateStatus: (
    id: string,
    body: { status: string; adminNotes?: string | null },
  ) => fetcher.patch(`${config.apiUrl}/class-enrollments/${id}/status`, body),

  similarStudents: (id: string) =>
    fetcher.get<{ ok: true; matches: ClassEnrollmentSimilarStudentDTO[] }>(
      `${config.apiUrl}/class-enrollments/${id}/similar-students`,
    ),

  convert: async (
    id: string,
    body: { linkExistingStudentId?: string; forceCreateNew?: boolean },
  ) => {
    const res = await fetch(`${config.apiUrl}/class-enrollments/${id}/convert`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    const data = (await res.json().catch(() => ({}))) as {
      ok?: boolean;
      studentId?: string;
      memberId?: string;
      linked?: boolean;
      code?: string;
      matches?: ClassEnrollmentSimilarStudentDTO[];
      error?: string;
    };
    if (!res.ok) {
      if (res.status === 409 && data.code === "SIMILAR_STUDENTS" && data.matches) {
        const err = new Error(data.code) as Error & {
          code: string;
          matches: ClassEnrollmentSimilarStudentDTO[];
        };
        err.code = data.code;
        err.matches = data.matches;
        throw err;
      }
      throw new Error(data.error || data.code || "convert failed");
    }
    return data;
  },
};
